import { Icon, Button } from "armstrong-react";
import classNames from "classnames";
import { LocationDescriptorObject } from "history";
import React from "react";
import { connect } from "react-redux";
import { browserHistory, Link } from "react-router";
import { push } from "react-router-redux";
import * as Redux from "redux";
import _ from "underscore";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import { Logo } from "../../../core/atoms";
import { AboutDropdown, DestinationDropdown, InspirationDropdown, LinkButton } from "@travellocal/ui";
import { isServer } from "../../../core/detection";
import { lockPageScroll, unlockPageScroll } from "../../../../../packages/ui/src/utils/uiHelpers";
import { RoutesInvoker } from "../../routesInvoker";
import { CookieBar } from "../cookieBar";
import { MobileMenu } from "../mobileMenu/mobileMenu";
import { CurrencySelector } from "./../currencySelector";
import { CollectionKeyHelper } from "../../../shared/helpers/contentCollectionHelpers";
import { ActionPromises } from "../../state/actionPromises";

import "./header.scss";

const getDropdownContent = (state: IWebStoreState, key: string) => {
  return state.contentByCollection.data?.[key]?.data?.[0] ?? null;
}

const stateToProps = (state: IWebStoreState) => {
  return {
    router: state.routing,
    user: state.user ? state.user.data : undefined,
    regions: state.regions,
    countries: state.countries,
    currentLto: state.lto,
    currentCountry: state.country,
    currentItin: state.sampleItinerary,
    dropdownContent: [
      getDropdownContent(state, CollectionKeyHelper.DropDownContentItineraries),
      getDropdownContent(state, CollectionKeyHelper.DropDownContentArticles),
      getDropdownContent(state, CollectionKeyHelper.DropDownContentReviews),
    ] as [IContent, IContent, IContent],
  };
};

const dispatchToProps = (dispatch: Redux.Dispatch<any>) => {
  return {
    navigate: (route: string) => dispatch(push(route)),
    refreshDropdownContent: () => dispatch(ActionPromises.getContentForDropdowns())
  };
};

type Props = ReturnType<typeof stateToProps> & ReturnType<typeof dispatchToProps>;

interface State {
  headerSticky?: boolean;
  mobileOverlayOpen?: boolean;
  headerExtrasHidden?: boolean;
}

class NewHeader extends React.Component<Props, State> {
  private unregisterBrowserHistory: () => void;

  public constructor(props: Props) {
    super(props);
    this.state = {
      headerExtrasHidden: false,
    };
  }

  private getCurrentPath = () =>
    this.props.router && this.props.router.locationBeforeTransitions
      ? (this.props.router.locationBeforeTransitions as LocationDescriptorObject).pathname
      : null

  private getEnquiryRoute = () => {
    const campaign = "header";

    const path = this.getCurrentPath();

    if (path) {
      // kinda brittle logic. enquire now footer used to be inserted by leaf components,
      // but because the menu button was moved to the bottom of the
      // page it now has to be inserted by this header component to avoid z-index problems

      if (path.indexOf(WebRoutes.itineraryRoutePrefix) === 0 && this.props.currentItin.data) {
        const itin = this.props.currentItin.data;
        return WebRoutes.enquiryForm({ locationSlug: itin?.primaryLocation?.slug, itineraryId: itin.id, campaign });
      }

      if (path.indexOf(WebRoutes.ltoRoutePrefix) === 0 && this.props.currentLto.data) {
        const lto = this.props.currentLto.data;
        if (lto.locations.length === 0) { // Some LTOs are no longer attached to any locations
          return WebRoutes.enquiryForm({ campaign })
        }
        else {
          return WebRoutes.enquiryForm({ locationSlug: lto.locations[0].slug, campaign });
        }
      }

      if (
        path.indexOf(WebRoutes.destinationRoutePrefix) === 0
        && this.props.currentCountry.data && path.split("/").indexOf(this.props.currentCountry.data.slug) === 3
      ) {
        return WebRoutes.enquiryForm({ locationSlug: this.props.currentCountry.data.slug, campaign });
      }
    }

    return WebRoutes.enquiryForm({ campaign });
  }

  private setHeaderExtrasVisibility = () => {
    if (window.pageYOffset > 100) {
      this.setState({
        headerExtrasHidden: true,
      });
    } else if (window.pageYOffset <= 100 && this.state.headerExtrasHidden) {
      this.setState({
        headerExtrasHidden: false,
      });
    }
  }

  private handleHeaderExtraseVisibility = _.throttle(this.setHeaderExtrasVisibility, 400);

  public componentWillMount() {
    if (isServer()) {
      return;
    }

    this.unregisterBrowserHistory = browserHistory?.listen(location => {
      const newState: State = {};

      if (this.state.mobileOverlayOpen) {
        this.closeMenuOverlay();
      }

      if (Object.keys(newState).length > 0) {
        setTimeout(() => this.setState(newState), 100); // Slight delay to cover up page load
      }
    });

    window.addEventListener("scroll", this.handleHeaderExtraseVisibility);
  }

  public componentWillUnmount() {
    if (isServer()) {
      return;
    }

    this.unregisterBrowserHistory();
  }

  private openMenuOverlay = () => {
    lockPageScroll();
    this.setState({ mobileOverlayOpen: true });
  }

  private closeMenuOverlay = () => {
    unlockPageScroll();
    requestAnimationFrame(() => this.setState({ mobileOverlayOpen: false }));
  }

  public render() {
    const { user, dropdownContent } = this.props;

    const signedInCustomer = user && user.userType === UserType.Customer
      ? user
      : null;
    const enquireNowRoute = this.getEnquiryRoute();

    const path = this.getCurrentPath();

    const customerArea = path?.includes("customer");

    return (
      <div className="site-header-wrap">
        <CookieBar />

        <header className={classNames("site-header")}>
          <a href="#main" className="is-sr-only">Skip to main content</a>
          <div className={classNames("header-extras", { hidden: this.state.headerExtrasHidden })}>
            <Logo link="/" />
            <div className="reviews">
              <a href="http://www.reviews.co.uk/company-reviews/store/travellocalcom" target="_blank">
                <img
                  className="review-stars"
                  alt="Check out our reviews from reviews.co.uk"
                  src="/images/sticky-footer/reviews-stars-inline.png"
                />
                <img
                  className="review-logo"
                  alt="Check out our reviews from reviews.co.uk"
                  src="/images/sticky-footer/reviews-logo-inline.png"
                />
              </a>
            </div>

            <ul className="benefits">
              <li>
                ABTA &amp; ATOL protected
              </li>
            </ul>

            <Link
              to={WebRoutes.aboutHowItWorks()}
              className="secondary-button text--label text--mid how-it-works"
            >
              <span>How it works</span>
            </Link>

            <div className="scallop">
              <div className="call-us">
                <a className="number secondary-button text--label text--mid" href="tel:+441173257898">
                  <span>+44 (0)117 325 7898</span>
                </a>
              </div>
              <CurrencySelector onSelect={this.props.refreshDropdownContent} />
              {signedInCustomer ? (
                <Link to={RoutesInvoker.logout} className="sign-out secondary-button text--label text--dark">
                  <span>Sign out</span>
                </Link>
              ) : null}

              <div className="mobile-header">
                {!customerArea &&
                  <Link
                    className="primary-button inverted heading--4 text--light"
                    to={enquireNowRoute}>Enquire</Link>
                }
                {customerArea && signedInCustomer &&
                  <Link
                    className="primary-button inverted heading--4 text--light"
                    to={RoutesInvoker.customerIndex}>My Trips</Link>
                }

                <Button
                  className="menu-hamburger text--label text--dark"
                  onClick={() => this.openMenuOverlay()}
                  aria-haspopup="true"
                  aria-expanded={this.state.mobileOverlayOpen}>
                  <Icon icon={Icon.Icomoon.menu7} />MENU
                </Button>
              </div>
            </div>
          </div>
          <div className="header-navigation">
            <Logo link="/" />

            <nav className="nav-items">
              <ul>
                <li>
                  <DestinationDropdown
                    regions={this.props.regions?.data ?? []}
                    countries={this.props.countries?.data ?? []}
                  />
                </li>
                <li>
                  <InspirationDropdown
                    content={dropdownContent}
                  />
                </li>
                <li>
                  <AboutDropdown />
                </li>
                {!customerArea &&
                  <li>
                    <LinkButton
                      className="enquire primary-button heading--4 text--light"
                      to={enquireNowRoute}>Enquire</LinkButton>
                  </li>
                }
                {signedInCustomer &&
                  <li>
                    <Link
                      className="account primary-button heading--4 text--light"
                      to={RoutesInvoker.customerIndex}>My trips</Link>
                  </li>
                }
                {!user &&
                  <li>
                    <Link
                      className="account primary-button heading--4 text--light"
                      to={RoutesInvoker.login()}>Sign in</Link>
                  </li>
                }
              </ul>
            </nav>
          </div>
        </header>

        <MobileMenu
          enquireRoute={enquireNowRoute}
          signedInCustomer={signedInCustomer}
          mobileOverlayOpen={this.state.mobileOverlayOpen}
          closeMenu={this.closeMenuOverlay}
          routing={this.props.router}
        />

      </div>
    );
  }
}

export const Header = connect(stateToProps, dispatchToProps)(NewHeader);
